/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { OnInputChangeEventContext, OnChangeEventContext } from 'react-crane'
import SearchIcon from '@mui/icons-material/Search'
import HistoryIcon from '@mui/icons-material/History'

import { Autocomplete } from '../../shared/input'
import { SearchStudent } from '../../../types'
import { DropdownProps } from '../../shared/input/Dropdown'
import { baseColor, borderColor, disabledIconColor } from '../../../lib/styles/colors'
import { StudentProfile } from '../../../generated/graphql'
import { getDisplayName } from '../../../lib/utils'

const adornmentCss = css`
  height: 0.01em;
  display: flex;
  max-height: 2em;
  align-items: center;
  white-space: nowrap;
  color: ${disabledIconColor};
  position: absolute;

  svg {
    padding-top: 18px;
  }
`

const Adornment = ({ children }: { children: React.ReactNode }) => (
  <div css={adornmentCss}>{children}</div>
)

const containerCss = css`
  padding: 6px 16px 0 0;
`

const searchAutocompleteCss = css`
  max-width: 500px;

  &.focus:not(.open) .crane-select-input-group,
  &:hover .crane-select-input-group {
    border: 1px solid ${baseColor};
  }

  .crane-select-value-group {
    padding-left: 16px;
    padding-top: 0;
  }

  &.open .crane-select-input-group:after {
    border-bottom: 1px solid ${borderColor};
    margin-left: 5px;
    margin-right: 5px;
  }

  .crane-select-input-group {
    background-color: #f5f6f9;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 10px;

    &:focus-within {
      background: white;
      border: 1px solid ${baseColor};
      border-bottom: none;
    }
  }

  .crane-select-input-group:after {
    border-bottom: none;
  }

  .crane-select-inner-input {
    position: relative;
    padding-left: 28px;
    width: 100%;
  }

  .crane-select-value-group {
    margin-top: 2px;
  }

  .crane-select-menu-container,
  .crane-select-no-results {
    border: 1px solid ${baseColor};
    border-top: none;
  }

  .crane-select-menu-container {
    padding-bottom: 4px;
  }
`
const historyStudentCss = css`
  display: flex;
  align-items: center;

  svg {
    display: inline-block;
    color: ${disabledIconColor};
    padding-right: 8px;
  }
`

type StudentSearchAutoCompleteProps = {
  /** Indicates that there are Previous Search results available */
  hasPreviousSearches: boolean
  /** Event callback when user selects a student in results */
  onChange: (e: OnChangeEventContext) => void
  /** Event callback when input in the Search AutoComplete changes */
  onInputChange: (e: OnInputChangeEventContext) => void
  /** Text to search students by for server side searching */
  searchText: string
  /** List of Student Results */
  studentProfiles: Array<StudentProfile>
} & DropdownProps

type OptionRendererParams = {
  option: SearchStudent
}

/** Note: this is duplicated in scheduler monolith. */
export const StudentSearchAutoComplete = ({
  hasPreviousSearches,
  onChange,
  onInputChange,
  searchText,
  studentProfiles,
  ...otherProps
}: StudentSearchAutoCompleteProps) => {
  const handleChange = (e: OnChangeEventContext) => onChange(e)

  const options = studentProfiles.map((s) => ({
    ...s,
    displayName: s ? `${s.firstName} ${s.lastName}` : ''
  }))

  const optionRenderer = ({ option }: OptionRendererParams) => {
    if (!option) {
      return ''
    }

    const studentName = getDisplayName(option)
    const displayName = `${studentName} - ${option.externalId}`
    return option.previouslySearched ? (
      <div css={historyStudentCss}>
        <HistoryIcon /> {displayName}
      </div>
    ) : (
      displayName
    )
  }

  const placeholderText = 'search for a student by name or student ID'
  return (
    <div css={containerCss}>
      <Autocomplete
        {...otherProps}
        id="search_students"
        aria-label={placeholderText}
        beforeInput={
          <Adornment>
            <SearchIcon />
          </Adornment>
        }
        css={searchAutocompleteCss}
        inputValue={searchText}
        label="Search Students"
        labelKey="displayName"
        onChange={handleChange}
        onInputChange={onInputChange}
        openOnEmptyInput={hasPreviousSearches}
        optionRenderer={optionRenderer}
        options={options}
        placeholder={placeholderText}
        srOnlyLabel
        valueKey="id"
      />
    </div>
  )
}

export default StudentSearchAutoComplete
